import { secureApi } from './secureApi';
import { clientApi } from './clientApi';

/**
 * Service API — Cleaning Service Module
 *
 * Dual-instance pattern:
 * - secureApi: JWT-authenticated ops/admin endpoints
 * - clientApi: Token-in-URL client portal endpoints (no JWT)
 *
 * Backend routes: be/routes/serviceRoutes.js (mounted at /api/services)
 */

const serviceApi = {

  // ==========================================
  // SERVICE CLIENTS (ops — JWT auth)
  // ==========================================

  createClient: async (clientData) => {
    try {
      const response = await secureApi.post('/api/services/clients', clientData);
      return response.data;
    } catch (error) {
      console.error('Error creating service client:', error);
      throw error;
    }
  },

  // Atomic onboarding — User + ServiceClient + Location (+ optional Subscription + Invoice)
  // Used by the CreateServiceClient wizard (Stage 6c).
  onboardClient: async (payload) => {
    try {
      const response = await secureApi.post('/api/services/clients/onboard', payload);
      return response.data;
    } catch (error) {
      console.error('Error onboarding service client:', error);
      throw error;
    }
  },

  getClients: async () => {
    try {
      const response = await secureApi.get('/api/services/clients');
      return response.data;
    } catch (error) {
      console.error('Error fetching service clients:', error);
      throw error;
    }
  },

  getClient: async (clientId) => {
    try {
      const response = await secureApi.get(`/api/services/clients/${clientId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching service client ${clientId}:`, error);
      throw error;
    }
  },

  updateClient: async (clientId, updates) => {
    try {
      const response = await secureApi.patch(`/api/services/clients/${clientId}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating service client ${clientId}:`, error);
      throw error;
    }
  },

  regenerateToken: async (clientId) => {
    try {
      const response = await secureApi.post(`/api/services/clients/${clientId}/regenerate-token`);
      return response.data;
    } catch (error) {
      console.error(`Error regenerating token for client ${clientId}:`, error);
      throw error;
    }
  },

  // ==========================================
  // SERVICE LOCATIONS (ops — JWT auth)
  // ==========================================

  createLocation: async (locationData) => {
    try {
      const response = await secureApi.post('/api/services/locations', locationData);
      return response.data;
    } catch (error) {
      console.error('Error creating service location:', error);
      throw error;
    }
  },

  getClientLocations: async (clientId) => {
    try {
      const response = await secureApi.get(`/api/services/clients/${clientId}/locations`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching locations for client ${clientId}:`, error);
      throw error;
    }
  },

  updateLocation: async (locationId, updates) => {
    try {
      const response = await secureApi.patch(`/api/services/locations/${locationId}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating location ${locationId}:`, error);
      throw error;
    }
  },

  // ==========================================
  // SUBSCRIPTIONS (ops — JWT auth)
  // ==========================================

  createSubscription: async (subscriptionData) => {
    try {
      const response = await secureApi.post('/api/services/subscriptions', subscriptionData);
      return response.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  },

  getClientSubscriptions: async (clientId) => {
    try {
      const response = await secureApi.get(`/api/services/clients/${clientId}/subscriptions`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscriptions for client ${clientId}:`, error);
      throw error;
    }
  },

  updateSubscription: async (subscriptionId, updates) => {
    try {
      const response = await secureApi.patch(`/api/services/subscriptions/${subscriptionId}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating subscription ${subscriptionId}:`, error);
      throw error;
    }
  },

  renewSubscription: async (subscriptionId, renewalData) => {
    try {
      const response = await secureApi.post(`/api/services/subscriptions/${subscriptionId}/renew`, renewalData);
      return response.data;
    } catch (error) {
      console.error(`Error renewing subscription ${subscriptionId}:`, error);
      throw error;
    }
  },

  // ==========================================
  // SERVICE REQUESTS (ops — JWT auth)
  // ==========================================

  getAllRequests: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.upcoming) params.set('upcoming', 'true');
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await secureApi.get(`/api/services/requests${query}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all service requests:', error);
      throw error;
    }
  },

  createRequest: async (requestData) => {
    try {
      const response = await secureApi.post('/api/services/requests', requestData);
      return response.data;
    } catch (error) {
      console.error('Error creating service request:', error);
      throw error;
    }
  },

  getClientRequests: async (clientId) => {
    try {
      const response = await secureApi.get(`/api/services/clients/${clientId}/requests`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching requests for client ${clientId}:`, error);
      throw error;
    }
  },

  cancelRequest: async (requestId) => {
    try {
      const response = await secureApi.patch(`/api/services/requests/${requestId}`, { status: 'cancelled' });
      return response.data;
    } catch (error) {
      console.error(`Error cancelling request ${requestId}:`, error);
      throw error;
    }
  },

  // ==========================================
  // CATALOG (Stage 4 — read-only pricing surface)
  // ==========================================

  getCatalog: async () => {
    try {
      const response = await secureApi.get('/api/services/catalog');
      return response.data;
    } catch (error) {
      console.error('Error fetching pricing catalog:', error);
      throw error;
    }
  },

  /**
   * Stateless quote calculator. Server returns commercials + packageSnapshot.
   *   filters: { (locationId | pricingTier), serviceType?, packageCode?, discountAmount? }
   * `pricingTier` is used by the wizard before the location is persisted.
   */
  getCatalogQuote: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.locationId) params.set('locationId', filters.locationId);
      if (filters.pricingTier) params.set('pricingTier', filters.pricingTier);
      if (filters.serviceType) params.set('serviceType', filters.serviceType);
      if (filters.packageCode) params.set('packageCode', filters.packageCode);
      if (filters.discountAmount !== undefined && filters.discountAmount !== '') {
        params.set('discountAmount', String(filters.discountAmount));
      }
      const response = await secureApi.get(`/api/services/catalog/quote?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching catalog quote:', error);
      throw error;
    }
  },

  // ==========================================
  // INVOICES (Stage 4 — financial CRUD surface)
  // ==========================================

  invoices: {
    list: async (filters = {}) => {
      try {
        const params = new URLSearchParams();
        ['status', 'clientId', 'clientName', 'from', 'to', 'q', 'page', 'pageSize'].forEach((k) => {
          if (filters[k] !== undefined && filters[k] !== '') params.set(k, String(filters[k]));
        });
        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await secureApi.get(`/api/services/invoices${query}`);
        return response.data;
      } catch (error) {
        console.error('Error listing invoices:', error);
        throw error;
      }
    },

    get: async (id) => {
      try {
        const response = await secureApi.get(`/api/services/invoices/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error fetching invoice ${id}:`, error);
        throw error;
      }
    },

    recordPayment: async (id, payload) => {
      try {
        const response = await secureApi.post(`/api/services/invoices/${id}/payment`, payload);
        return response.data;
      } catch (error) {
        console.error(`Error recording payment for invoice ${id}:`, error);
        throw error;
      }
    },

    recordRefund: async (id, payload) => {
      try {
        const response = await secureApi.post(`/api/services/invoices/${id}/refunds`, payload);
        return response.data;
      } catch (error) {
        console.error(`Error recording refund for invoice ${id}:`, error);
        throw error;
      }
    },

    cancel: async (id, reason) => {
      try {
        const response = await secureApi.patch(`/api/services/invoices/${id}/cancel`, { reason });
        return response.data;
      } catch (error) {
        console.error(`Error cancelling invoice ${id}:`, error);
        throw error;
      }
    },

    /** Returns a CSV string with UTF-8 BOM. Caller wraps in Blob to download. */
    exportCsv: async (filters = {}) => {
      try {
        const params = new URLSearchParams();
        ['status', 'clientId', 'clientName', 'from', 'to', 'q'].forEach((k) => {
          if (filters[k] !== undefined && filters[k] !== '') params.set(k, String(filters[k]));
        });
        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await secureApi.get(`/api/services/invoices/export.csv${query}`, {
          responseType: 'text'
        });
        return response.data;
      } catch (error) {
        console.error('Error exporting invoices CSV:', error);
        throw error;
      }
    }
  },

  // ==========================================
  // CLIENT PORTAL (token auth — no JWT)
  // ==========================================

  clientPortal: {
    getDashboard: async (token) => {
      try {
        const response = await clientApi.get(`/api/services/client-portal/${token}/dashboard`);
        return response.data;
      } catch (error) {
        console.error('Error fetching client dashboard:', error);
        throw error;
      }
    },

    submitRequest: async (token, requestData) => {
      try {
        const response = await clientApi.post(`/api/services/client-portal/${token}/requests`, requestData);
        return response.data;
      } catch (error) {
        console.error('Error submitting client request:', error);
        throw error;
      }
    },

    getRequests: async (token) => {
      try {
        const response = await clientApi.get(`/api/services/client-portal/${token}/requests`);
        return response.data;
      } catch (error) {
        console.error('Error fetching client requests:', error);
        throw error;
      }
    },

    cancelRequest: async (token, requestId) => {
      try {
        const response = await clientApi.patch(`/api/services/client-portal/${token}/requests/${requestId}`, { status: 'cancelled' });
        return response.data;
      } catch (error) {
        console.error('Error cancelling client request:', error);
        throw error;
      }
    },

    updateProfile: async (token, profileData) => {
      try {
        const response = await clientApi.patch(`/api/services/client-portal/${token}/profile`, profileData);
        return response.data;
      } catch (error) {
        console.error('Error updating client profile:', error);
        throw error;
      }
    },

    getVisits: async (token, page = 1, limit = 10) => {
      try {
        const response = await clientApi.get(`/api/services/client-portal/${token}/visits?page=${page}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching client visits:', error);
        throw error;
      }
    }
  }
};

export default serviceApi;
